/**
 * NarratorShareCard Component
 *
 * Shareable card featuring Narrator voice with episodic framing.
 * Designed for entertainment-focused sharing ("Episode Title" format).
 *
 * ## Visual Design
 * - **Theme**: Purple (#F5F3FF background, #9333EA accents)
 * - **Typography**: Courier New (episodic/screenwriting aesthetic)
 * - **Layout**: Episode title + drama score + attribution
 * - **Dimensions**: 1080×1920px (9:16 Instagram Stories)
 *
 * ## Content Structure
 * ```
 * ┌────────────────────────┐
 * │  "Episode N: Title"    │ (Large, bold, Courier New)
 * │                        │
 * │  "Drama Score: X/10"   │ (Medium, colored by score)
 * │                        │
 * │  Context badge         │ (Work/Dating/Family/Friends)
 * │                        │
 * │  "Narrated by your     │ (Small, italic attribution)
 * │   story's observer"    │
 * └────────────────────────┘
 * ```
 *
 * ## Usage
 * ```tsx
 * <NarratorShareCard
 *   episodeTitle="Episode 5: The Budget Battle"
 *   dramaScore={75}
 *   context="work"
 * />
 * ```
 *
 * Created: 2025-01-11 (Task 2.2: Viral Share Formats)
 */

import React from "react";
import { View, Text, StyleSheet } from "react-native";
import { useTranslation } from "@dramadetector/shared";
import type { MessageContext } from "@dramadetector/shared";

/**
 * Props for NarratorShareCard component
 */
export interface NarratorShareCardProps {
	/**
	 * Episode title (from generateEpisodeTitle)
	 * Example: "Episode 5: The Budget Battle"
	 */
	episodeTitle: string;

	/**
	 * Drama score (0-10 scale from backend)
	 * Example: 7.5 → "7.5/10"
	 */
	dramaScore: number;

	/**
	 * Message context (work, dating, family, friends)
	 * Used to display context badge
	 * @default undefined (no badge shown)
	 */
	context?: MessageContext;

	/**
	 * Optional test ID for E2E testing
	 * @default 'narrator-share-card'
	 */
	testID?: string;
}

/**
 * Get drama score color based on 0-10 scale
 */
function getDramaScoreColor(score: number): string {
	if (score <= 2) return "#10B981"; // Green (safe)
	if (score <= 5) return "#F59E0B"; // Yellow (caution)
	return "#EF4444"; // Red (high_drama/extreme_drama)
}

/**
 * Convert context to translation key
 */
function getContextKey(context: MessageContext | undefined): string | null {
	if (!context) return null;
	return `share_card.context.${context}`;
}

export function NarratorShareCard({
	episodeTitle,
	dramaScore,
	context,
	testID = "narrator-share-card",
}: NarratorShareCardProps) {
	const { t } = useTranslation("share", { useSuspense: false }) as {
		t: (key: string, options?: any) => string;
	};
	const scoreColor = getDramaScoreColor(dramaScore);
	const contextKey = getContextKey(context);
	const scoreDisplay = dramaScore.toFixed(1); // 7.5 → "7.5"

	return (
		<View style={styles.container} testID={testID}>
			{/* Episode Title */}
			<Text style={styles.episodeTitle} testID={`${testID}-title`}>
				{episodeTitle}
			</Text>

			{/* Drama Score */}
			<View style={styles.scoreContainer}>
				<Text style={[styles.scoreLabel, { color: scoreColor }]}>
					{t("share_card.narrator.score_label")}
				</Text>
				<Text style={[styles.scoreValue, { color: scoreColor }]}>
					{scoreDisplay}/10
				</Text>
			</View>

			{/* Context Badge (if provided) */}
			{contextKey && (
				<View style={styles.contextBadge} testID={`${testID}-context`}>
					<Text style={styles.contextText}>{t(contextKey as any)}</Text>
				</View>
			)}

			{/* Narrator Attribution */}
			<Text style={styles.attribution} testID={`${testID}-attribution`}>
				{t("share_card.narrator.attribution")}
			</Text>

			{/* Decorative Quote Marks (visual flair) */}
			<Text style={styles.decorativeQuote}>{'"'}</Text>
		</View>
	);
}

const styles = StyleSheet.create({
	container: {
		flex: 1,
		flexDirection: "column",
		alignItems: "center",
		justifyContent: "center",
		paddingHorizontal: 40,
		paddingVertical: 60,
	},
	episodeTitle: {
		fontSize: 48,
		fontWeight: "700",
		color: "#581C87", // Purple-900
		textAlign: "center",
		marginBottom: 40,
		fontFamily: "Courier New", // Episodic typewriter aesthetic
		lineHeight: 56,
	},
	scoreContainer: {
		flexDirection: "row",
		alignItems: "baseline",
		marginBottom: 32,
	},
	scoreLabel: {
		fontSize: 24,
		fontWeight: "600",
		marginRight: 8,
		fontFamily: "Courier New",
	},
	scoreValue: {
		fontSize: 56,
		fontWeight: "700",
		fontFamily: "Courier New",
	},
	contextBadge: {
		backgroundColor: "#9333EA", // Purple-600
		paddingHorizontal: 24,
		paddingVertical: 12,
		borderRadius: 24,
		marginBottom: 48,
	},
	contextText: {
		fontSize: 18,
		fontWeight: "600",
		color: "#FFFFFF",
		fontFamily: "Inter",
	},
	attribution: {
		fontSize: 16,
		fontStyle: "italic",
		color: "#A855F7", // Purple-500
		textAlign: "center",
		marginTop: 32,
		fontFamily: "Courier New",
	},
	decorativeQuote: {
		position: "absolute",
		top: 20,
		left: 20,
		fontSize: 120,
		fontWeight: "700",
		color: "#E9D5FF", // Purple-200 (subtle)
		opacity: 0.3,
		fontFamily: "Georgia", // Serif for decorative quote
	},
});
